import type { UseFormRegister, FieldErrors, Path, FieldValues, UseFormSetValue, Control } from 'react-hook-form'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { CepInput } from './cep-input'
import { FormSection } from './form-section'

interface AddressFieldsProps<T extends FieldValues> {
  register: UseFormRegister<T>
  errors: FieldErrors<T>
  setValue: UseFormSetValue<T>
  control: Control<T>
  zipCodeName: Path<T>
  streetName: Path<T>
  numberName: Path<T>
  complementName: Path<T>
  neighborhoodName: Path<T>
  cityName: Path<T>
  stateName: Path<T>
  disabled?: boolean
}

export function AddressFields<T extends FieldValues>({
  register,
  errors,
  setValue,
  control,
  zipCodeName,
  streetName,
  numberName,
  complementName,
  neighborhoodName,
  cityName,
  stateName,
  disabled,
}: AddressFieldsProps<T>) {
  type ErrorValue = { message?: string }

  function err(name: Path<T>) {
    const e = errors[name] as ErrorValue | undefined
    return e?.message
  }

  return (
    <FormSection title="Endereço">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <CepInput
          control={control}
          name={zipCodeName}
          disabled={disabled}
          onAddressFilled={(data) => {
            setValue(streetName, data.street as Parameters<typeof setValue>[1])
            setValue(neighborhoodName, data.neighborhood as Parameters<typeof setValue>[1])
            setValue(cityName, data.city as Parameters<typeof setValue>[1])
            setValue(stateName, data.state as Parameters<typeof setValue>[1])
          }}
        />
        <div className="space-y-1.5">
          <Label htmlFor={String(numberName)}>Número</Label>
          <Input
            id={String(numberName)}
            aria-invalid={!!err(numberName)}
            disabled={disabled}
            {...register(numberName)}
          />
          {err(numberName) && <p className="text-xs text-destructive">{err(numberName)}</p>}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={String(streetName)}>Logradouro</Label>
        <Input
          id={String(streetName)}
          aria-invalid={!!err(streetName)}
          disabled={disabled}
          {...register(streetName)}
        />
        {err(streetName) && <p className="text-xs text-destructive">{err(streetName)}</p>}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={String(complementName)}>Complemento</Label>
          <Input
            id={String(complementName)}
            placeholder="Apto, sala…"
            disabled={disabled}
            {...register(complementName)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={String(neighborhoodName)}>Bairro</Label>
          <Input
            id={String(neighborhoodName)}
            aria-invalid={!!err(neighborhoodName)}
            disabled={disabled}
            {...register(neighborhoodName)}
          />
          {err(neighborhoodName) && <p className="text-xs text-destructive">{err(neighborhoodName)}</p>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <div className="col-span-2 space-y-1.5">
          <Label htmlFor={String(cityName)}>Cidade</Label>
          <Input
            id={String(cityName)}
            aria-invalid={!!err(cityName)}
            disabled={disabled}
            {...register(cityName)}
          />
          {err(cityName) && <p className="text-xs text-destructive">{err(cityName)}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={String(stateName)}>UF</Label>
          <Input
            id={String(stateName)}
            maxLength={2}
            placeholder="SP"
            aria-invalid={!!err(stateName)}
            disabled={disabled}
            {...register(stateName)}
          />
          {err(stateName) && <p className="text-xs text-destructive">{err(stateName)}</p>}
        </div>
      </div>
    </FormSection>
  )
}
